import React from 'react';
import {
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Button
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import StorageIcon from '@mui/icons-material/Storage';
import BackupIcon from '@mui/icons-material/Backup';
import ShareIcon from '@mui/icons-material/Share';
import SecurityIcon from '@mui/icons-material/Security';

const services = [
  'NAS device installation',
  'RAID configuration',
  'Automated backup setup',
  'File sharing configuration',
  'User access management',
  'Data migration services',
  'Cloud integration setup',
  'Remote access configuration',
  'Storage monitoring',
  'Disaster recovery planning'
];

const benefits = [
  {
    icon: StorageIcon,
    title: 'Reliable Storage',
    description: 'Enterprise-grade NAS solutions'
  },
  {
    icon: BackupIcon,
    title: 'Secure Backup',
    description: 'Automated data protection'
  },
  {
    icon: ShareIcon,
    title: 'Easy Sharing',
    description: 'Efficient file collaboration'
  },
  {
    icon: SecurityIcon,
    title: 'Data Security',
    description: 'Advanced encryption and protection'
  }
];

const storageFeatures = [
  'RAID Storage',
  'File Sharing',
  'Data Backup',
  'Cloud Sync',
  'Remote Access',
  'User Management',
  'Data Protection',
  'Storage Monitoring',
  'Mobile Access',
  'Version Control'
];

function NetworkStorage() {
  return (
    <Box sx={{ py: 6, bgcolor: 'background.default' }}>
      {/* Hero Section */}
      <Container maxWidth="lg">
        <Box sx={{ textAlign: 'center', mb: 8 }}>
          <Typography
            variant="h2"
            component="h1"
            className="gradient-text"
            sx={{ mb: 3 }}
          >
            Network Storage Solutions
          </Typography>
          <Typography
            variant="h5"
            sx={{ color: 'text.secondary', mb: 4 }}
          >
            Professional NAS setup and data management services
          </Typography>
          <Button
            component={RouterLink}
            to="/booking"
            variant="contained"
            size="large"
            sx={{
              py: 2,
              px: 4,
              fontSize: '1.2rem',
              background: 'linear-gradient(45deg, #7c3aed 30%, #8b5cf6 90%)',
              '&:hover': {
                background: 'linear-gradient(45deg, #8b5cf6 30%, #7c3aed 90%)',
              }
            }}
          >
            Schedule Storage Setup
          </Button>
        </Box>

        {/* Benefits Section */}
        <Grid container spacing={4} sx={{ mb: 8 }}>
          {benefits.map((benefit, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Card className="glass-effect card-hover" sx={{ height: '100%' }}>
                <CardContent sx={{ textAlign: 'center', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                  <Box sx={{ mb: 2, color: 'secondary.main' }}>
                    {React.createElement(benefit.icon, { sx: { fontSize: 40 } })}
                  </Box>
                  <Typography variant="h6" gutterBottom>
                    {benefit.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {benefit.description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Services List */}
        <Paper 
          elevation={3} 
          sx={{ 
            p: 4, 
            mb: 8,
            background: 'rgba(255, 255, 255, 0.05)',
            backdropFilter: 'blur(10px)'
          }}
        >
          <Typography variant="h4" gutterBottom sx={{ mb: 4 }}>
            Our Storage Services
          </Typography>
          <Grid container spacing={2}>
            {services.map((service, index) => (
              <Grid item xs={12} sm={6} key={index}>
                <List>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircleIcon color="secondary" />
                    </ListItemIcon>
                    <ListItemText primary={service} />
                  </ListItem>
                </List>
              </Grid>
            ))}
          </Grid>
        </Paper>

        {/* Storage Features Section */}
        <Paper 
          elevation={3} 
          sx={{ 
            p: 4,
            background: 'rgba(255, 255, 255, 0.05)',
            backdropFilter: 'blur(10px)'
          }}
        >
          <Typography variant="h4" gutterBottom sx={{ mb: 4 }}>
            Storage Features
          </Typography>
          <Grid container spacing={2}>
            {storageFeatures.map((feature, index) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
                <Card 
                  className="glass-effect"
                  sx={{ 
                    textAlign: 'center',
                    py: 2
                  }}
                >
                  <Typography variant="h6">
                    {feature}
                  </Typography>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Paper>
      </Container>
    </Box>
  );
}

export default NetworkStorage;
